import React from "react";
import { axiosInstance } from "../../utils/axios";

export type Mode = "orders" | "totals";

export type DispatchSummary = {
    id: string;
    sellerName: string;
    clientName: string;
    packedByName: string;
    dispatchStatus: string;
    scheduledAt?: string;
};

export type DispatchDetailItem = {
    id: string;
    name: string;
    qty: number;
};

export type TotalsItem = {
    productId: string;
    name: string;
    totalQty: number;
};

function todayISO() {
    const d = new Date();
    return d.toISOString().slice(0, 10);
}

export function useDispatchData() {
    const [selectedDate, setSelectedDate] = React.useState<string>(todayISO());
    const [mode, setMode] = React.useState<Mode>("orders");

    const [dispatches, setDispatches] = React.useState<DispatchSummary[]>([]);
    const [totals, setTotals] = React.useState<TotalsItem[]>([]);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedOrder, setSelectedOrder] = React.useState<DispatchSummary | null>(null);
    const [detailItems, setDetailItems] = React.useState<DispatchDetailItem[] | null>(null);

    const [loading, setLoading] = React.useState<boolean>(false);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [version, setVersion] = React.useState<number>(0);
    const [updating, setUpdating] = React.useState<boolean>(false);

    // fetch list or totals when date or mode changes
    React.useEffect(() => {
        const ac = new AbortController();
        async function fetchData() {
            setLoading(true);
            setError(null);
            try {
                if (mode === "orders") {
                    const res = await axiosInstance.get<DispatchSummary[]>(`/dispatches?date=${selectedDate}`, { signal: ac.signal });
                    setDispatches(res.data);
                } else {
                    const res = await axiosInstance.get<TotalsItem[]>(`/dispatches/totals?date=${selectedDate}`, { signal: ac.signal });
                    setTotals(res.data);
                }
            } catch (error) {
                if (error instanceof Error) {
                    setError(error.message);
                }
            } finally {
                setLoading(false);
            }
        }
        fetchData();
        return () => {
            ac.abort();
        };
    }, [selectedDate, mode, version]);

    // fetch detail when selectedId changes
    React.useEffect(() => {
        if (!selectedId) {
            setSelectedOrder(null);
            setDetailItems(null);
            setDetailLoading(false);
            return;
        }
        const ac = new AbortController();
        async function fetchDetail() {
            setDetailLoading(true);
            setError(null);
            try {
                const res = await axiosInstance.get<DispatchDetailItem[]>(`/dispatches/${selectedId}`, { signal: ac.signal });
                setDetailItems(res.data);
            } catch (error) {
                if (error instanceof Error) {
                    setError(error.message);
                }
            } finally {
                setDetailLoading(false);
            }
        }
        fetchDetail();
        setSelectedOrder(dispatches.find(d => d.id === selectedId) || null);
        return () => {
            ac.abort();
        };
    }, [selectedId, dispatches]);

    const refresh = React.useCallback(() => {
        // bump version to force refetch in effects
        setVersion(v => v + 1);
    }, []);

    // update dispatch status (no optimistic update)
    const updateDispatchStatus = React.useCallback(async (id: string, newStatus: string, packedById: string | null) => {
        setUpdating(true);
        try {
            await axiosInstance.patch(`/dispatches/${id}`, { dispatchStatus: newStatus, packedById });
            // refresh to ensure consistent data from server
            refresh();
        } finally {
            setUpdating(false);
        }
    }, [refresh]);

    return {
        selectedDate,
        setSelectedDate,
        mode,
        setMode,
        dispatches,
        totals,
        selectedId,
        setSelectedId,
        selectedOrder,
        detailItems,
        loading,
        detailLoading,
        error,
        refresh,
        updateDispatchStatus,
        updating
    };
}
